import { len2 } from "../core/math.js";
import { BODY_CHECK, modeSpeed, type Ball, type Player, type Vec2 } from "@golazo/shared";

/**
 * Rival mas cercano dentro del alcance y por delante de ti.
 *
 * Se exige direccion: embestir al que tienes al lado o detras no es poner el
 * cuerpo, es un empujon gratis. Si no hay input de movimiento se usa hacia
 * donde miras.
 */
function findChargeTarget(
  player: Player,
  opponents: readonly Player[],
  move: Vec2 | null,
): Player | null {
  let dirX = move?.x ?? 0;
  let dirY = move?.y ?? 0;
  const len = len2(dirX, dirY);
  if (len > 1e-3) {
    dirX /= len;
    dirY /= len;
  } else {
    dirX = player.facing.x;
    dirY = player.facing.y;
  }

  let best: Player | null = null;
  let bestDist = Infinity;
  for (const rival of opponents) {
    const dx = rival.pos.x - player.pos.x;
    const dy = rival.pos.y - player.pos.y;
    const dist = len2(dx, dy);
    if (dist > BODY_CHECK.REACH || dist < 1e-3) continue;
    if ((dx / dist) * dirX + (dy / dist) * dirY < BODY_CHECK.CONE_COS) continue;
    if (dist < bestDist) {
      best = rival;
      bestDist = dist;
    }
  }
  return best;
}

/**
 * Como acabo el intento de poner el cuerpo.
 *
 * `blocked` es el caso interesante: el rival llevaba el balon y habia plantado
 * el cuerpo (su O) dentro de la ventana, asi que el que rebota eres tu. Se
 * distingue de `null` —no habia nadie a quien embestir— porque el juego lo
 * cuenta distinto: uno es un gesto al aire y el otro es un duelo perdido.
 */
export interface BodyCheckResult {
  victim: Player;
  blocked: boolean;
}

/**
 * Poner el cuerpo sobre el rival cercano. Devuelve a quien has embestido, o
 * null si no habia nadie a quien embestir.
 *
 * Deliberadamente NO toca la posesion. El rival se queda el balon; lo que
 * pierde es el sitio y el equilibrio, y es su control degradado el que hace que
 * se le escape. Quitarselo aqui convertiria esto en una segunda entrada.
 *
 * Si no hay objetivo no gasta enfriamiento: es contextual, como el caño. Un
 * boton que se come el enfriamiento al aire castiga por intentarlo. Y como el
 * enfriamiento es justo lo que `stepPlayer` mira para decidir si dashea, ese
 * "no gastar" es tambien lo que convierte la O en carrera cuando no hay a quien
 * embestir: aqui se decide cual de los dos verbos sale.
 */
export function applyBodyCheck(
  player: Player,
  opponents: readonly Player[],
  move: Vec2 | null,
  ball: Ball | null = null,
  ballOwnerId: string | null = null,
): BodyCheckResult | null {
  if (player.dashCooldown > 0 || player.trapTime > 0 || player.stumbleTime > 0) return null;

  const target = findChargeTarget(player, opponents, move);
  if (!target) return null;

  /*
   * Se embiste al hueco ENTRE su cuerpo y el balon, no a su centro.
   *
   * El balon conducido va pegado delante de quien lo lleva. Apuntando al centro
   * del rival le empujas por la espalda: se mueve, pero el balon sigue siendo
   * suyo y tu sigues detras. Apuntando al hueco, el cuerpo se mete donde de
   * verdad se gana una posicion — que es literalmente lo que en futbol se llama
   * poner el cuerpo.
   *
   * Solo cuando lleva el balon: contra un rival sin balon no hay hueco que
   * ocupar, y desviar el embiste ahi solo lo haria menos fiable.
   */
  let aimX = target.pos.x;
  let aimY = target.pos.y;
  if (ball && ballOwnerId === target.id) {
    const bx = ball.pos.x - target.pos.x;
    const by = ball.pos.y - target.pos.y;
    const toBall = len2(bx, by);
    if (toBall > 1e-3) {
      const lead = Math.min(BODY_CHECK.SHIELD_LEAD, toBall);
      aimX += (bx / toBall) * lead;
      aimY += (by / toBall) * lead;
    }
  }

  const dx = aimX - player.pos.x;
  const dy = aimY - player.pos.y;
  const dist = len2(dx, dy) || 1;
  const nx = dx / dist;
  const ny = dy / dist;

  player.dashCooldown = BODY_CHECK.COOLDOWN;

  /*
   * El rival tenia el cuerpo plantado: rebotas tu.
   *
   * Es la mitad que hace que el cuerpo sea un duelo y no un boton ganador. El
   * que embiste apuesta a que el otro no lo ve venir; si lo ve, sale despedido
   * en la direccion en la que venia, que es lo que hace que se lea como un
   * choque y no como un empujon del defensor.
   *
   * La ventana se CONSUME: protegerse para un cuerpo no te cubre del siguiente.
   */
  if (target.braceTime > 0) {
    target.braceTime = 0;
    const bounce = modeSpeed(BODY_CHECK.BLOCK_SHOVE);
    player.vel.x = -nx * bounce;
    player.vel.y = -ny * bounce;
    player.stumbleTime = Math.max(player.stumbleTime, BODY_CHECK.BLOCK_STUMBLE);
    return { victim: target, blocked: true };
  }

  const shove = modeSpeed(BODY_CHECK.SHOVE);
  target.vel.x += nx * shove;
  target.vel.y += ny * shove;
  target.stumbleTime = Math.max(target.stumbleTime, BODY_CHECK.STUMBLE_TIME);

  const lunge = modeSpeed(BODY_CHECK.LUNGE_SPEED);
  player.vel.x = nx * lunge;
  player.vel.y = ny * lunge;
  player.facing.x = nx;
  player.facing.y = ny;
  player.stumbleTime = Math.max(player.stumbleTime, BODY_CHECK.SELF_STUMBLE);

  return { victim: target, blocked: false };
}
